/**
 * useV2AppointmentDialogLegacy — Feb 2026.
 *
 * Reusable hook that builds the `legacy` prop bag for
 * `AppointmentScheduleDialog` so V2 surfaces (CalendarRoute, the V2
 * client chart Appts tab, etc.) can mount the dialog natively without
 * duplicating ~150 lines of helper construction.
 *
 * Source-of-truth implementation was lifted out of
 * `routes/CalendarRoute.jsx` (the V2 native Calendar) which has been
 * battle-tested in production.  Keeping the helpers in a single place
 * means future tweaks to access-control / dialog wiring land in one
 * file.
 *
 * Usage:
 *   const refreshAppointments = useCallback(async () => { ... }, []);
 *   const legacy = useV2AppointmentDialogLegacy({ refreshAppointments });
 *   return <AppointmentScheduleDialog legacy={legacy} />;
 *
 * The hook only owns helpers / access-control logic.  Form-state
 * (showAppointmentForm, editingAppointment, appointmentFormData) still
 * lives in AppointmentsContext, and the consumer is responsible for
 * `setShowAppointmentForm(true)` to actually open the dialog.
 */
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

import { useAuth } from '../context/AuthContext';
import { useAppointments } from '../context/AppointmentsContext';
import { useStaticData } from '../context/StaticDataContext';
import { useClients } from '../context/ClientsContext';import { isSuperAdmin, getEffectivePosCodes } from '../utils/helpers';
import { getAccessibleProvidersShared } from '../utils/providerAccess';
import { formatClientName, sortClientsByName } from '../utils/nameFormatter';
import {
  getEligibilityDisplay as sharedGetEligibilityDisplay,
  getDOSEligibilityDisplay as sharedGetDOSEligibilityDisplay,
  eligibilityTooltip as sharedEligibilityTooltip,
} from '../utils/eligibilityDisplay';
import { useClientEligibility } from './useClientEligibility';
import { applyEligibilityPatch } from '../utils/eligibilityPatch';
import { validateCptDuration } from '../utils/appointmentDurationRules';

const API = process.env.REACT_APP_BACKEND_URL;

export const useV2AppointmentDialogLegacy = ({ refreshAppointments }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    showAppointmentForm: _show,
    setShowAppointmentForm,
    editingAppointment,
    setEditingAppointment,
    appointmentFormData,
    setAppointments,
    showGroupNoteDialog,
    setShowGroupNoteDialog,
    groupNoteGroup,
    setGroupNoteGroup,
    groupNoteCurriculumSession,
    setGroupNoteCurriculumSession,
    groupNoteExistingNotes,
    setGroupNoteExistingNotes,
    groupNoteMode,
    setGroupNoteMode,
    setGroupNoteAppointment,
    setUndoStack,
  } = useAppointments();
  const { providers, users, cptCodes, cptBundles } = useStaticData();
  const { clients, allClientsForLookup, setClients, setAllClientsForLookup } = useClients();

  // Stable safe refresher.  Caller may pass a no-op for surfaces that
  // don't need to refetch (e.g., when the AppointmentsContext is
  // already kept in sync by another listener).
  const safeRefresh = useCallback(async () => {
    try {
      if (typeof refreshAppointments === 'function') {
        await refreshAppointments();
      }
    } catch (e) {
      console.warn('[useV2AppointmentDialogLegacy] refresh failed', e);
    }
  }, [refreshAppointments]);

  const closeAppointment = useCallback(() => {
    setEditingAppointment(null);
    setShowAppointmentForm(false);
  }, [setEditingAppointment, setShowAppointmentForm]);

  const handleCreateAppointment = useCallback(
    async (e) => {
      e?.preventDefault?.();
      try {
        const form = appointmentFormData || {};
        if (form.appointment_type === 'client_session') {
          if (!form.provider_id) return toast.error('Client sessions require a provider');
          if (!form.client_id) return toast.error('Client sessions require a client');
          if (!form.service_type) return toast.error('Client sessions require a CPT code');
          if (!form.location) return toast.error('Client sessions require a location');
        }
        if (!form.start_time || !form.end_time) {
          return toast.error('Start and end times are required');
        }
        // Jun 2026 — enforce CPT duration limits on MANUAL edits (same
        // rules the resize handler enforces); blocks the save with a
        // toast so nothing beyond the limits is ever persisted.
        {
          const durErr = validateCptDuration(form, cptCodes);
          if (durErr) return toast.error(durErr);
        }
        const payload = { ...form };
        if (typeof payload.start_time === 'string' && !payload.start_time.endsWith('Z')) {
          payload.start_time = new Date(payload.start_time).toISOString();
        }
        if (typeof payload.end_time === 'string' && !payload.end_time.endsWith('Z')) {
          payload.end_time = new Date(payload.end_time).toISOString();
        }
        delete payload.selectedCptMeta;
        delete payload.editingAppointmentId;
        delete payload._reschedule_context;
        // Jun 2026 (user report: "can't schedule appts from the client
        // appts tab") — port the legacy payload sanitization (App.js
        // ~L17200).  The chart prefill seeds recurrence_count:'' /
        // recurrence_end_date:'' which Pydantic rejects (422: "input is
        // too short" / "unable to parse string as an integer").
        {
          const unitsInt = parseInt(payload.units, 10);
          payload.units = Number.isInteger(unitsInt) && unitsInt >= 1 ? unitsInt : 1;
        }
        if (payload.session_number === '' || payload.session_number === null) {
          delete payload.session_number;
        }
        if (!payload.is_recurring) {
          delete payload.recurrence_pattern;
          delete payload.recurrence_end_date;
          delete payload.recurrence_count;
          delete payload.recurring_days;
        } else {
          const rc = parseInt(payload.recurrence_count, 10);
          if (Number.isInteger(rc) && rc >= 1) payload.recurrence_count = rc;
          else delete payload.recurrence_count;
          if (!payload.recurrence_end_date) {
            delete payload.recurrence_end_date;
          } else if (isNaN(new Date(payload.recurrence_end_date).getTime())) {
            return toast.error('Recurrence end date is incomplete — please pick a full date.');
          }
        }
        let savedDoc = null;
        if (editingAppointment?.id) {
          const r = await axios.patch(`${API}/api/appointments/${editingAppointment.id}`, payload);
          // PATCH envelope: ``{message, appointment: <full doc>}`` since
          // the Feb 2026 backend fix.  Fall back to spreading the
          // payload when the server is older or returns an array.
          savedDoc = r?.data?.appointment || r?.data || null;
          toast.success('Appointment updated');
          // Optimistic update — replace the cached row with the server
          // doc so any newly-attached fields (incl. auto-created
          // ``google_meet_link`` when modality changed to telehealth)
          // show immediately without waiting for the refresh round-trip.
          if (savedDoc && savedDoc.id && typeof setAppointments === 'function') {
            setAppointments((prev) => (prev || []).map((apt) =>
              apt.id === savedDoc.id ? { ...apt, ...savedDoc } : apt
            ));
          }
        } else {
          const r = await axios.post(`${API}/api/appointments`, payload);
          savedDoc = r?.data || null;
          toast.success('Appointment scheduled');
          // Optimistic add — the backend auto-creates the Google Meet
          // link inline when ``pos_code in ['02', '10']`` (see
          // routers/appointments.py::_create_single_appointment ~L955)
          // so the response doc already carries ``google_meet_link``.
          // Pushing it into AppointmentsContext immediately makes the
          // V2 chart's Appts tab show the "Join" button without
          // requiring the user to re-open the appt.  Mirrors the
          // legacy Dashboard's post-create state push (App.js ~L17056).
          if (savedDoc && savedDoc.id && typeof setAppointments === 'function') {
            setAppointments((prev) => {
              const exists = (prev || []).some((a) => a.id === savedDoc.id);
              return exists
                ? (prev || []).map((a) => (a.id === savedDoc.id ? { ...a, ...savedDoc } : a))
                : [...(prev || []), savedDoc];
            });
          }
        }
        setShowAppointmentForm(false);
        setEditingAppointment(null);
        await safeRefresh();
        // Reschedule task auto-close — same hook the CalendarRoute uses.
        try {
          const ctx = (appointmentFormData || {})._reschedule_context;
          if (ctx?.task_id) {
            const mod = await import('../utils/rescheduleTaskCompletion');
            const cr = await mod.completeRescheduleTaskAfterBooking({
              ctx, newAppointment: savedDoc, apiBase: `${API}/api`, axios,
            });
            if (cr.commentOk && cr.statusOk) toast.success('Reschedule task closed automatically');
            else if (cr.commentOk || cr.statusOk) toast.info('Appointment booked — task partially closed (see Tasks tab)');
          }
        } catch (closeErr) {
          console.warn('[v2 appt dialog] reschedule auto-close failed', closeErr);
        }
      } catch (err) {
        const detail = err?.response?.data?.detail || err?.message || 'Failed to save appointment';
        toast.error(typeof detail === 'string' ? detail : 'Failed to save appointment');
      }
    },
    [appointmentFormData, editingAppointment, setShowAppointmentForm, setEditingAppointment, safeRefresh, cptCodes],
  );

  const openMeetPopup = useCallback((aptOrLink) => {
    // Accepts either an appointment object or a raw link string —
    // AppointmentScheduleDialog passes the link itself (Jun 2026 fix:
    // the object-only signature made Start Session a silent no-op on V2).
    const link = typeof aptOrLink === 'string' ? aptOrLink : aptOrLink?.google_meet_link;
    if (link) {
      window.open(link, '_blank', 'noopener,noreferrer');
    }
  }, []);
  const openNote = useCallback((note) => {
    if (!note?.id) return;
    // Phase D (Sep 2026) — native V2 note editor with a return path
    // back to the current page (was a /dashboard?view=notes bounce).
    const ret = encodeURIComponent(window.location.pathname + window.location.search);
    navigate(`/v2/note/${note.id}?return=${ret}`);
  }, [navigate]);

  const userRoleName = (user?.role || '').toLowerCase();
  const isUserAdmin = isSuperAdmin(user) || userRoleName === 'admin' || !!user?.is_billing_manager;

  const getAccessibleProviders = useCallback((appointmentType = null) => {
    // Role-based filtering restored to match legacy behavior (Oct 2026) —
    // shared with CalendarRoute via utils/providerAccess.
    return getAccessibleProvidersShared({ user, userRoleName, users, providers, appointmentType });
  }, [user, userRoleName, providers, users]);

  const getAccessibleClients = useCallback(
    (providerId) => {
      if (!user) return [];
      if (!providerId) return [];
      const clientList = allClientsForLookup?.length > 0 ? allClientsForLookup : (clients || []);
      const activeClients = clientList.filter((c) => c.status !== 'archived' && c.status !== 'merged');
      let filtered = activeClients.filter(
        (c) => c.assigned_providers && c.assigned_providers.includes(providerId),
      );
      // Jun 2026 — alphabetize by "LASTNAME, First" (display order).
      filtered = sortClientsByName(filtered);
      // Ensure the currently-selected client (edit OR schedule prefill)
      // is always present in the dropdown — `getAccessibleClients`
      // otherwise filters by `assigned_providers` and would hide a
      // chart's client that isn't formally assigned to the picked
      // provider.  Without this branch, opening Schedule from a V2
      // client chart shows an empty Client dropdown even though
      // appointmentFormData.client_id is set correctly.
      const currentClientId = appointmentFormData?.client_id || editingAppointment?.client_id;
      if (currentClientId) {
        const present = filtered.some((c) => c.id === currentClientId);
        if (!present) {
          const fullList = allClientsForLookup || [];
          let current = fullList.find((c) => c.id === currentClientId);
          if (!current && clients?.length > 0) current = clients.find((c) => c.id === currentClientId);
          if (current) {
            filtered = [current, ...filtered];
          } else if (editingAppointment?.client_name) {
            // Edit-mode fallback — synthesise a placeholder row using
            // the snapshot we have on the appt itself.  Schedule mode
            // has no such snapshot, so the dialog will see the option
            // missing — but at minimum the controlled value matches an
            // option once the chart pushes the full client list.
            filtered = [{
              id: currentClientId,
              full_name: editingAppointment.client_name,
              first_name: editingAppointment.client_name?.split(' ')[0] || '',
              last_name: editingAppointment.client_name?.split(' ').slice(1).join(' ') || '',
              status: 'unknown',
            }, ...filtered];
          }
        }
      }
      return filtered;
    },
    [user, clients, allClientsForLookup, appointmentFormData, editingAppointment],
  );

  const getAccessibleCPTCodes = useCallback(
    (providerId = null) => {
      if (!user) return [];
      const resolveForId = (pid) => {
        const provider = (users || []).find((u) => u.id === pid)
                       || (providers || []).find((p) => p.id === pid);
        if (!provider) return [];
        const individualIds = provider.assigned_cpt_codes || provider.cpt_codes || [];
        const bundleIds = provider.assigned_cpt_bundles || [];
        const assignedBundles = (cptBundles || []).filter(
          (b) => (b.assigned_provider_ids && b.assigned_provider_ids.includes(pid)) || bundleIds.includes(b.id),
        );
        const bundleCptIds = assignedBundles.flatMap((b) => b.cpt_code_ids || []);
        const all = new Set([...individualIds, ...bundleCptIds]);
        return (cptCodes || []).filter((c) => all.has(c.id));
      };
      if (providerId) {
        const filtered = resolveForId(providerId);
        if (filtered.length === 0 && isUserAdmin) return cptCodes || [];
        return filtered;
      }
      if (isUserAdmin || userRoleName === 'office') return cptCodes || [];
      if (userRoleName === 'provider') return resolveForId(user.id);
      return cptCodes || [];
    },
    [user, users, providers, cptBundles, cptCodes, isUserAdmin, userRoleName],
  );

  const noop = useCallback(() => {}, []);
  // Jun 2026 V2 parity — eligibility re-check + delete-undo were noop-stubbed
  // (dead "↻ Refresh" link and un-undoable deletes on every V2 surface).
  const applyEligibilityResult = useCallback((clientId, patch, insuranceIndex) => {
    applyEligibilityPatch({ setClients, setAllClientsForLookup }, clientId, patch, insuranceIndex);
  }, [setClients, setAllClientsForLookup]);
  const { handleEligibilityCheck } = useClientEligibility({ selectedClient: null, applyEligibilityResult });
  const addToUndoStack = useCallback((action, data) => {
    setUndoStack((prev) => [...prev, { action, data, timestamp: Date.now() }].slice(-20));
  }, [setUndoStack]);
  // Mar 2026 — same bug fix as CalendarRoute.jsx: the intake-bundle
  // picker in AppointmentScheduleDialog silently no-op'd when opened
  // via this legacy bridge because the setter was ``noop``.  Backed
  // by real state now so V2-native callers get the picker too.
  const [applicableIntakeBundles, setApplicableIntakeBundles] = useState([]);
  const noopAsync = useCallback(async () => {}, []);

  return useMemo(() => ({
    isSuperAdmin,
    getEffectivePosCodes,
    closeAppointment,
    fetchAppointments: safeRefresh,
    handleCreateAppointment,
    refreshAppointments: safeRefresh,
    // v2-na: V2 surfaces own their notes/todos/stats refetching
    refreshNotes: noopAsync,
    refreshTodos: noopAsync,
    refreshStats: noop,
    openMeetPopup,
    openNote,
    navigate,
    getAccessibleClients,
    getAccessibleProviders,
    getAccessibleCPTCodes,
    getDOSEligibilityDisplay: sharedGetDOSEligibilityDisplay,
    getEligibilityDisplay: sharedGetEligibilityDisplay,
    eligibilityTooltip: sharedEligibilityTooltip,
    handleEligibilityCheck,
    handleOpenAttendanceDialog: null,
    formatClientName,
    addToUndoStack,
    safeEligibilityDetail: null,
    applicableIntakeBundles,
    setApplicableIntakeBundles,
    setChangeHistoryEntity: null,
    setShowChangeHistory: null,
    showGroupNoteDialog,
    setShowGroupNoteDialog,
    groupNoteGroup,
    setGroupNoteGroup,
    groupNoteCurriculumSession,
    setGroupNoteCurriculumSession,
    groupNoteExistingNotes,
    setGroupNoteExistingNotes,
    groupNoteMode,
    setGroupNoteMode,
    setGroupNoteAppointment,
    allAuthorizations: [],
  }), [
    closeAppointment, handleCreateAppointment, safeRefresh,
    openMeetPopup, openNote, navigate,
    getAccessibleClients, getAccessibleProviders, getAccessibleCPTCodes,
    showGroupNoteDialog, setShowGroupNoteDialog,
    groupNoteGroup, setGroupNoteGroup,
    groupNoteCurriculumSession, setGroupNoteCurriculumSession,
    groupNoteExistingNotes, setGroupNoteExistingNotes,
    groupNoteMode, setGroupNoteMode, setGroupNoteAppointment,
    noop, noopAsync, handleEligibilityCheck, addToUndoStack,
  ]);
};

export default useV2AppointmentDialogLegacy;
